import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Block, SpacerBlockData } from '../../../config';
import './spacer-block.scss';

interface SpacerBlockProps {
  block: Block;
  onChange: (data: SpacerBlockData) => void;
  onDelete: () => void;
  isEditing: boolean;
}

type SpacerHeight = 'small' | 'medium' | 'large';

const HEIGHT_OPTIONS: { value: SpacerHeight; label: string }[] = [
  { value: 'small', label: 'S' },
  { value: 'medium', label: 'M' },
  { value: 'large', label: 'L' },
];

const SpacerBlock: React.FC<SpacerBlockProps> = ({ block, onChange, isEditing }) => {
  const data = block.data as SpacerBlockData;
  const [showToolbar, setShowToolbar] = useState(false);
  const blockRef = useRef<HTMLDivElement>(null);

  const handleHeightChange = useCallback((height: SpacerHeight) => {
    onChange({ ...data, height });
  }, [data, onChange]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isEditing) {
      setShowToolbar(true);
    }
  }, [isEditing]);

  // Close toolbar when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (blockRef.current && !blockRef.current.contains(e.target as Node)) {
        setShowToolbar(false);
      }
    };

    if (showToolbar) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showToolbar]);

  return (
    <div 
      ref={blockRef}
      className={`spacer-block ${showToolbar ? 'focused' : ''}`}
      onClick={handleClick}
      tabIndex={0}
    >
      {/* Toolbar */}
      {showToolbar && isEditing && (
        <div className="spacer-toolbar">
          <div className="toolbar-group">
            <span className="group-label">Size</span>
            <div className="toolbar-buttons">
              {HEIGHT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`toolbar-btn ${data.height === opt.value ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleHeightChange(opt.value);
                  }}
                  title={opt.value}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className={`spacer height-${data.height}`}>
        {isEditing && (
          <span className="spacer-label">Spacer</span>
        )}
      </div>
    </div>
  );
};

export default SpacerBlock;
